import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, MonitorPlay, Gamepad2, Tv, Music, LayoutGrid, ChevronRight } from 'lucide-react';
import { getCategories } from '../firebase/categories';
import { Category } from '../types';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesData = await getCategories();
      // Filter main categories (no parentId) and sort by order (Hot first)
      setCategories(
        categoriesData
          .filter(c => !c.parentId)
          .sort((a, b) => {
            if (a.name === 'Hot') return -1;
            if (b.name === 'Hot') return 1;
            return (a.order || 999) - (b.order || 999);
          })
      );
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryName: string) => {
    navigate(`/categoria/${categoryName.toLowerCase()}`);
    onClose();
  };

  // Helper to get icon for category
  const getCategoryIcon = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('cine') || lowerName.includes('pelicula')) return MonitorPlay;
    if (lowerName.includes('juego') || lowerName.includes('game')) return Gamepad2;
    if (lowerName.includes('tv') || lowerName.includes('tele')) return Tv;
    if (lowerName.includes('musica') || lowerName.includes('spotify')) return Music;
    return LayoutGrid;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-md bg-black border border-neon-blue/30 rounded-2xl shadow-[0_0_50px_rgba(0,255,255,0.15)] overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Glow Effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-neon-blue to-transparent" />
        
        <div className="p-8">
          <div className="text-center mb-8">
            <h2 className="font-orbitron text-2xl font-bold text-white mb-2">
              ¿Qué estás buscando?
            </h2>
            <p className="text-gray-400 font-rajdhani">
              Selecciona una categoría para empezar
            </p>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="h-14 bg-white/5 rounded-xl" />
                ))}
              </div>
            ) : (
              categories.map((category) => {
                const Icon = getCategoryIcon(category.name);
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category.name)}
                    className="w-full flex items-center gap-4 p-4 bg-white/5 hover:bg-neon-blue/10 border border-white/10 hover:border-neon-blue/50 rounded-xl transition-all duration-300 group text-left"
                  >
                    <div className="p-2 bg-black rounded-lg text-neon-blue group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="font-orbitron font-bold text-lg text-white group-hover:text-neon-blue transition-colors tracking-wide">
                      {category.name}
                    </span>
                    <ChevronRight className="ml-auto w-5 h-5 text-gray-500 group-hover:text-neon-blue group-hover:translate-x-1 transition-all" />
                  </button>
                );
              })
            )}
          </div>

          <button
            onClick={onClose}
            className="w-full mt-8 py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-500 font-rajdhani font-bold rounded-xl transition-all"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
